import { useCallback, useEffect, useId, useState } from 'react';
import { can, MIN_REASON } from '@urbanrats/ops-core';
import type { DevKey, DevView } from '../backend/types.ts';
import { PageHead, Section, StateTag } from '../components/common.tsx';
import { ChangeRequests } from '../components/ChangeRequests.tsx';
import { fmtTime, useCtx } from '../lib.ts';

type DevTab = 'start' | 'keys' | 'wallet' | 'webhooks' | 'test' | 'reference';
type StepState = 'done' | 'pending' | 'todo' | 'optional';

/**
 * Developer portal, organised as a guided integration: a checklist of the steps with their live state
 * (keys, wallet, sites, webhooks, test, certification) and one tab per step. INTEGRATION_READY (our suite
 * passed) and PARTNER_CERTIFIED (the partner's acceptance) stay separate.
 */
export function Developer() {
  const { backend, user } = useCtx();
  const dev = backend.developer;
  const [view, setView] = useState<DevView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [operatorId, setOperatorId] = useState<string | undefined>(undefined);
  const [version, setVersion] = useState(0);
  const [tab, setTab] = useState<DevTab>('start');
  const load = useCallback(async () => setVersion((n) => n + 1), []);
  useEffect(() => {
    if (!dev) return;
    let live = true;
    void dev.get(operatorId).then((r) => {
      if (!live) return;
      if (r.ok) {
        setView(r.value);
        setErr(null);
      } else setErr(r.error);
    });
    return () => {
      live = false;
    };
  }, [dev, operatorId, version]);

  if (!dev)
    return (
      <>
        <PageHead title="Developer portal">Keys, webhooks and conformance live on the backend.</PageHead>
        <p className="empty" data-testid="dev-demo-note">
          This portal needs the UrbanRats backend (npm run api:dev and the admin in API mode). The demo has no partner credentials, webhooks or conformance runs, so nothing here is simulated.
        </p>
      </>
    );
  if (err)
    return (
      <p role="alert" className="field-error">
        {err}
      </p>
    );
  if (!view) return <p aria-busy="true">Loading…</p>;
  const v = view;
  const pending = backend.requests().filter((r) => r.kind === 'PARTNER_CONNECTION' && r.status === 'PENDING' && r.payload.operatorId === v.operatorId);
  const activeKeys = v.keys.filter((k) => k.state === 'ACTIVE' || k.state === 'EXPIRING');
  const direct = v.wallet.mode === 'DIRECT' && !!v.wallet.baseUrl;
  const steps: { tab: DevTab; title: string; state: StepState; now: string; next: string }[] = [
    {
      tab: 'keys',
      title: 'API key',
      state: activeKeys.length ? 'done' : 'todo',
      now: activeKeys.length ? `${activeKeys.length} active key${activeKeys.length === 1 ? '' : 's'}` : 'no key yet',
      next: 'Register your own Ed25519 public key (recommended) or let us generate a key. Your server signs every call with it.',
    },
    {
      tab: 'wallet',
      title: 'Wallet connection',
      state: direct && v.wallet.secretsSet ? 'done' : pending.length ? 'pending' : 'todo',
      now: direct ? `${v.wallet.baseUrl}${v.wallet.secretsSet ? ' · secrets set' : ' · secrets missing'}` : pending.length ? 'request waiting for UrbanRats approval' : 'using the UrbanRats demo wallet',
      next: 'Send us your wallet URL (we approve it) and generate the signing secrets. Until then players use our demo wallet — fine for first tests.',
    },
    {
      tab: 'reference',
      title: 'Sites',
      state: v.brands.length ? 'done' : 'optional',
      now: v.brands.length ? `${v.brands.length} site${v.brands.length === 1 ? '' : 's'}` : 'none yet',
      next: 'Aggregators: sites are created automatically on their first launch (send your site id as brandId and the currency). Direct operators: set up by UrbanRats.',
    },
    {
      tab: 'webhooks',
      title: 'Webhooks',
      state: v.endpoints.length ? 'done' : 'optional',
      now: v.endpoints.length ? `${v.endpoints.length} endpoint${v.endpoints.length === 1 ? '' : 's'}` : 'optional',
      next: 'Optional: receive bet and reward events on your server (register the host in the Wallet tab first).',
    },
    {
      tab: 'test',
      title: 'Conformance test',
      state: v.conformanceRuns[0]?.passed ? 'done' : 'todo',
      now: v.conformanceRuns[0] ? (v.conformanceRuns[0].passed ? 'last run passed' : 'last run failed') : 'not run yet',
      next: 'Run our automatic checks against your wallet with a test player. Passing = integration ready.',
    },
    {
      tab: 'test',
      title: 'Certification',
      state: v.integration?.status === 'PARTNER_CERTIFIED' ? 'done' : 'todo',
      now: v.integration?.status === 'PARTNER_CERTIFIED' ? 'certified' : 'after a joint acceptance test',
      next: 'Recorded by UrbanRats after we test together. Real money stays off until then.',
    },
  ];
  const doneN = steps.filter((x) => x.state === 'done').length;
  const tabs: [DevTab, string][] = [
    ['start', 'Get started'],
    ['keys', '1 · API key'],
    ['wallet', `2 · Wallet${pending.length ? ' •' : ''}`],
    ['webhooks', '3 · Webhooks'],
    ['test', '4 · Test'],
    ['reference', 'Reference'],
  ];
  return (
    <>
      <PageHead title="Developer portal">
        Everything a partner needs to connect Chaos Lane: follow the steps below. Full guide: <a href="https://api.urbanrats.com/integration">api.urbanrats.com/integration</a> · API reference: <a href="https://api.urbanrats.com/reference">/reference</a>
      </PageHead>
      {v.operators.length > 1 && (
        <label className="inline-field">
          Operator{' '}
          <select value={v.operatorId} onChange={(e) => setOperatorId(e.target.value)} data-testid="dev-operator">
            {v.operators.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
      )}
      {pending.length > 0 && can(user, 'onboarding.manage') && (
        <div className="warn-box dv-approve" role="status">
          <strong>{pending.length} connection request{pending.length === 1 ? '' : 's'} from this partner wait for your approval.</strong>{' '}
          <button type="button" className="btn" onClick={() => setTab('wallet')}>
            Review in the Wallet tab →
          </button>
        </div>
      )}
      <nav className="op-tabs" role="tablist" aria-label="Integration steps">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)} data-testid={`dev-tab-${k}`}>
            {l}
          </button>
        ))}
      </nav>
      {tab === 'start' && (
        <>
          <Section title={`Your integration · ${doneN} of ${steps.length} done`} testId="dev-steps">
            <ol className="dv-steps">
              {steps.map((st, i) => (
                <li key={st.title} className={`dv-step dv-${st.state}`}>
                  <span className="dv-mark" aria-hidden>
                    {st.state === 'done' ? '✓' : st.state === 'pending' ? '…' : i + 1}
                  </span>
                  <div className="dv-body">
                    <div className="dv-head">
                      <strong>{st.title}</strong>
                      <span className={`tag dv-tag-${st.state}`}>{st.state === 'done' ? 'done' : st.state === 'pending' ? 'waiting for approval' : st.state === 'optional' ? 'optional' : 'to do'}</span>
                      <span className="muted">{st.now}</span>
                    </div>
                    <p>{st.next}</p>
                  </div>
                  <button type="button" className="btn ghost" onClick={() => setTab(st.tab)}>
                    Open
                  </button>
                </li>
              ))}
            </ol>
          </Section>
          <Quickstart apiBase={dev.apiBase} />
        </>
      )}
      {tab === 'keys' && <Keys v={v} reload={load} />}
      {tab === 'wallet' && (
        <>
          <Connection v={v} reload={load} />
          {can(user, 'onboarding.manage') && (
            <Section title="Register a host directly (UrbanRats staff)" testId="dev-hosts">
              <p className="note">Staff shortcut without a request. Partners use the request above.</p>
              <HostForm operatorId={v.operatorId} reload={load} />
            </Section>
          )}
        </>
      )}
      {tab === 'webhooks' && <Webhooks v={v} reload={load} />}
      {tab === 'test' && (
        <>
          <Conformance v={v} reload={load} />
          <Integration v={v} />
        </>
      )}
      {tab === 'reference' && (
        <>
          <Section title="Your sites, currencies and games" testId="dev-entitlements">
            {v.brands.length === 0 ? (
              <p className="empty">No site yet. Aggregators: sites appear here after their first launch.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Site (brandId)</th>
                      <th>Wallet currency</th>
                      <th>Player countries</th>
                      <th>Games</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.brands.map((b) => (
                      <tr key={b.brandId}>
                        <td className="mono">{b.brandId}</td>
                        <td>{b.walletCurrency}</td>
                        <td>{b.countries.join(', ') || 'none'}</td>
                        <td>{b.games.map((g) => `${g.gameId}${g.enabled ? '' : ' (off)'}`).join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
          <Section title="Error codes and what to do" testId="dev-errors">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>HTTP</th>
                    <th>Retry?</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(v.errors).map(([code, e]) => (
                    <tr key={code}>
                      <td className="mono">{code}</td>
                      <td>{e.status}</td>
                      <td>{e.retry === 'NO' ? 'no' : e.retry === 'AFTER_FIX' ? 'after fixing' : 'yes, new nonce'}</td>
                      <td>{e.meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </>
  );
}

function Integration({ v }: { v: DevView }) {
  const i = v.integration;
  return (
    <Section title="Integration status and certification" testId="dev-integration">
      <dl className="kv">
        <dt>Adapter</dt>
        <dd>{i?.adapter ?? '—'}</dd>
        <dt>Status</dt>
        <dd data-testid="dev-integration-status">
          <StateTag state={i?.status ?? 'NOT_STARTED'} /> {i?.status === 'INTEGRATION_READY' && <span className="note">Our conformance suite passed. This is not a partner certification.</span>}
          {i?.status === 'PARTNER_CERTIFIED' && (
            <span className="note">
              Recorded from {i.certifiedBy}: {i.certificationEvidence}
            </span>
          )}
        </dd>
      </dl>
      <details>
        <summary>Wallet protocol adapters</summary>
      <div className="table-wrap">
        <table data-testid="dev-adapters">
          <thead>
            <tr>
              <th>Adapter</th>
              <th>Status</th>
              <th>Amount / scale</th>
              <th>Status mapping</th>
              <th>Round id</th>
              <th>Promo</th>
              <th>Duplicates</th>
            </tr>
          </thead>
          <tbody>
            {v.adapters.map((a) => (
              <tr key={a.id}>
                <td className="mono">{a.id}</td>
                <td>
                  <StateTag state={a.status} />
                </td>
                <td>
                  {a.mapping.amount} {a.mapping.scale}
                </td>
                <td>{a.mapping.status}</td>
                <td>{a.mapping.roundId}</td>
                <td>{a.mapping.promo}</td>
                <td>{a.mapping.duplicates}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </details>
    </Section>
  );
}

function Quickstart({ apiBase }: { apiBase: string }) {
  return (
    <Section title="How a game launch works" testId="dev-quickstart">
      <ol>
        <li>Your server calls <code>POST /v1/launch-sessions</code>, signed with your API key: site (brandId), your player id, game and the player’s country.</li>
        <li>You open the returned <code>launchUrl</code> in an iframe for the player (single use, 60 s).</li>
        <li>When the player bets, we call your wallet: <code>/bet</code> takes the stake, <code>/win</code> pays the result (0 for a loss), <code>/rollback</code> refunds a cancelled round.</li>
      </ol>
      <details>
        <summary>Code example (Node.js SDK)</summary>
        <pre className="code" data-testid="dev-snippet">{`import { PartnerClient } from '@urbanrats/partner-sdk';
const ur = new PartnerClient({ baseUrl: '${apiBase}', keyId: 'sbx-…', privateKeyPem: process.env.UR_PRIVATE_KEY });
const r = await ur.call('POST', '/v1/launch-sessions', { brandId: 'your-site', externalPlayerId: 'p-1', gameId: 'traffic-pulse', country: 'DE', currency: 'EUR' });
// r.body.launchUrl → open in an iframe`}</pre>
        <p className="note">Signing details and test vectors: api.urbanrats.com/integration (section 01).</p>
      </details>
    </Section>
  );
}

function useReason() {
  const [reason, setReason] = useState('');
  return { reason, setReason, ok: reason.trim().length >= MIN_REASON };
}

function Keys({ v, reload }: { v: DevView; reload: () => Promise<void> }) {
  const { backend, user } = useCtx();
  const dev = backend.developer!;
  const { reason, setReason, ok } = useReason();
  const [secret, setSecret] = useState<{
    keyId: string;
    privateKeyPem?: string;
    hmac?: string;
  } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [pub, setPub] = useState('');
  const id = useId();
  const act = async (path: string, body: Record<string, unknown>) => {
    const r = await dev.post<{
      keyId: string;
      privateKeyPem?: string;
      secret?: string;
    }>(path, { ...body, operatorId: v.operatorId, reason });
    if (!r.ok) return setMsg(r.error);
    setMsg(r.value.privateKeyPem || r.value.secret ? null : `Key ${r.value.keyId} registered with your public key.`);
    setReason('');
    setPub('');
    if (r.value.privateKeyPem) setSecret({ keyId: r.value.keyId, privateKeyPem: r.value.privateKeyPem });
    else if (r.value.secret) setSecret({ keyId: r.value.keyId, hmac: r.value.secret });
    await reload();
  };
  const mayManage = can(user, 'dev.keys');
  const live = v.keys.filter((k) => k.state !== 'REVOKED' && k.state !== 'EXPIRED');
  const old = v.keys.length - live.length;
  return (
    <Section title="API key" testId="dev-keys">
      <p className="lead-text">Your server signs every call to our API with this key. You need one active key; keep the private part only on your server.</p>
      {mayManage && (
        <div className="form inline">
          <label htmlFor={id}>Note for the audit log (min {MIN_REASON} chars)</label>
          <input id={id} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. sandbox key for our integration team" data-testid="dev-key-reason" />
        </div>
      )}
      {mayManage && (
        <div className="dv-choices">
          <div className="dv-choice">
            <h3>A · Use your own key <span className="tag dv-tag-done">recommended</span></h3>
            <p className="muted">Generate an Ed25519 key pair on your side and paste only the public key. The private key never leaves your server.</p>
            <textarea id={`${id}-pub`} rows={3} className="mono" value={pub} onChange={(e) => setPub(e.target.value)} placeholder={'-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----'} aria-label="Your Ed25519 public key (PEM)" data-testid="dev-own-pub" />
            <pre className="code small">openssl genpkey -algorithm ed25519 -out private.pem{'\n'}openssl pkey -in private.pem -pubout</pre>
            <button type="button" className="btn primary" disabled={!ok || !pub.includes('BEGIN PUBLIC KEY')} onClick={() => void act('/v1/admin/developer/keys', { label: 'sandbox (own key)', publicKeyPem: pub.trim() })} data-testid="dev-register-pub">
              Register public key
            </button>
          </div>
          <div className="dv-choice">
            <h3>B · Let us generate one</h3>
            <p className="muted">We create the key and show the secret part to you once. Choose HMAC only if your stack cannot sign with Ed25519.</p>
            <div className="op-actions">
              <button type="button" className="btn" disabled={!ok} onClick={() => void act('/v1/admin/developer/keys', { label: 'sandbox' })} data-testid="dev-issue-key">
                Generate Ed25519 key
              </button>
              <button type="button" className="btn" disabled={!ok} onClick={() => void act('/v1/admin/developer/keys', { label: 'sandbox (HMAC)', algorithm: 'HMAC-SHA256' })} data-testid="dev-issue-hmac">
                Generate HMAC key
              </button>
            </div>
          </div>
        </div>
      )}
      {msg && (
        <p role="alert" className="field-error">
          {msg}
        </p>
      )}
      {secret && (
        <div className="warn-box" role="status" data-testid="dev-secret">
          {secret.hmac ? (
            <>
              <strong>HMAC secret for {secret.keyId} — shown once. Copy it now into your server's secret store.</strong>
              <pre className="code">{secret.hmac}</pre>
            </>
          ) : (
            <>
              <strong>Private key for {secret.keyId} — shown once, not stored by UrbanRats. Copy it now.</strong>
              <pre className="code">{secret.privateKeyPem}</pre>
            </>
          )}
          <button type="button" className="btn" onClick={() => setSecret(null)}>
            I stored it — hide
          </button>
        </div>
      )}
      <h3>Your keys</h3>
      {live.length === 0 ? (
        <p className="empty">No active key yet.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Key id (x-ur-key-id)</th>
                <th>Type</th>
                <th>State</th>
                <th>Last used</th>
                {mayManage && <th />}
              </tr>
            </thead>
            <tbody>
              {live.map((k: DevKey) => (
                <tr key={k.keyId} data-testid={`key-${k.keyId}`} title={`scopes: ${k.scopes.join(' ')} · IP: ${k.ipAllowlist.join(', ') || 'any'} · created ${fmtTime(k.createdAt)}${k.expiresAt ? ` · expires ${fmtTime(k.expiresAt)}` : ''}`}>
                  <td className="mono">{k.keyId}</td>
                  <td>
                    {k.algorithm === 'HMAC-SHA256' ? 'HMAC' : 'Ed25519'} · {k.environment === 'SANDBOX' ? 'sandbox' : 'production'}
                  </td>
                  <td>
                    <StateTag state={k.state} />
                    {k.expiresAt && <span className="muted"> until {fmtTime(k.expiresAt)}</span>}
                  </td>
                  <td>{k.lastUsedAt ? fmtTime(k.lastUsedAt) : 'never'}</td>
                  {mayManage && (
                    <td>
                      {k.environment === 'SANDBOX' && (
                        <button type="button" className="btn ghost" disabled={!ok} title={ok ? 'New key; this one keeps working for 1 hour' : 'Write a note for the audit log first'} onClick={() => void act(`/v1/admin/developer/keys/${encodeURIComponent(k.keyId)}/rotate`, { overlapS: 3600 })} data-testid={`rotate-${k.keyId}`}>
                          Replace
                        </button>
                      )}{' '}
                      <button type="button" className="btn ghost danger" disabled={!ok} title={ok ? 'Stops working immediately' : 'Write a note for the audit log first'} onClick={() => void act(`/v1/admin/developer/keys/${encodeURIComponent(k.keyId)}/revoke`, {})} data-testid={`revoke-${k.keyId}`}>
                        Revoke
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {old > 0 && <p className="muted">{old} revoked or expired key{old === 1 ? '' : 's'} hidden.</p>}
      <p className="note">Going live: you send your production public key; a second UrbanRats person approves it. We never see your production private key.</p>
    </Section>
  );
}

function HostForm({ operatorId, reload }: { operatorId: string; reload: () => Promise<void> }) {
  const { backend } = useCtx();
  const [host, setHost] = useState('');
  const [purpose, setPurpose] = useState<'WALLET' | 'WEBHOOK'>('WEBHOOK');
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <form
      className="form inline"
      onSubmit={async (e) => {
        e.preventDefault();
        const r = await backend.developer!.post('/v1/admin/developer/hosts', {
          operatorId,
          host,
          purpose,
          reason,
        });
        setMsg(r.ok ? `Registered ${host}` : r.error);
        if (r.ok) {
          setHost('');
          setReason('');
          await reload();
        }
      }}
    >
      <label>
        Host <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="hooks.partner.example" data-testid="dev-host" />
      </label>
      <label>
        Purpose{' '}
        <select value={purpose} onChange={(e) => setPurpose(e.target.value as 'WALLET' | 'WEBHOOK')}>
          <option>WEBHOOK</option>
          <option>WALLET</option>
        </select>
      </label>
      <label>
        Reason <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dev-host-reason" />
      </label>
      <button type="submit" className="btn" disabled={!ok || !host}>
        Register host
      </button>
      {msg && <span role="status">{msg}</span>}
    </form>
  );
}

function Webhooks({ v, reload }: { v: DevView; reload: () => Promise<void> }) {
  const { backend, user } = useCtx();
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <Section title="Webhooks" testId="dev-webhooks">
      <p className="note">Endpoints are registered with a signed POST /v1/webhooks. Deliveries are at-least-once; a replay re-sends the same eventId.</p>
      {v.endpoints.length === 0 ? (
        <p className="empty">No endpoints.</p>
      ) : (
        <ul>
          {v.endpoints.map((e) => (
            <li key={e.endpointId}>
              <span className="mono">{e.url}</span> · {e.events.join(', ')} · {e.active ? 'active' : 'disabled'}
            </li>
          ))}
        </ul>
      )}
      <h3>Delivery log</h3>
      {v.deliveries.length === 0 ? (
        <p className="empty">No deliveries yet.</p>
      ) : (
        <div className="table-wrap">
          <table data-testid="dev-deliveries">
            <thead>
              <tr>
                <th>Created</th>
                <th>Event</th>
                <th>Event id</th>
                <th>State</th>
                <th>Attempts (HTTP / error)</th>
                {can(user, 'dev.webhooks') && <th>Replay</th>}
              </tr>
            </thead>
            <tbody>
              {v.deliveries.map((d) => (
                <tr key={d.deliveryId}>
                  <td>{fmtTime(d.createdAt)}</td>
                  <td>{d.eventType}</td>
                  <td className="mono">
                    {d.eventId}
                    {d.replayOf ? ' (replay)' : ''}
                  </td>
                  <td>
                    <StateTag state={d.state} />
                  </td>
                  <td>{d.log.map((a) => `${a.httpStatus ?? '—'}${a.error ? ` ${a.error}` : ''} (${a.durationMs} ms)`).join(' → ') || '—'}</td>
                  {can(user, 'dev.webhooks') && (
                    <td>
                      <button
                        type="button"
                        className="btn"
                        onClick={async () => {
                          const r = await backend.developer!.post<{
                            deliveryId: string;
                          }>(`/v1/admin/developer/deliveries/${encodeURIComponent(d.deliveryId)}/replay`, {});
                          setMsg(r.ok ? `Replay queued as ${r.value.deliveryId}` : r.error);
                          await reload();
                        }}
                      >
                        Replay
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {msg && <p role="status">{msg}</p>}
    </Section>
  );
}

function Conformance({ v, reload }: { v: DevView; reload: () => Promise<void> }) {
  const { backend, user } = useCtx();
  const [player, setPlayer] = useState('');
  const [currency, setCurrency] = useState(v.brands[0]?.walletCurrency ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const last = v.conformanceRuns[0];
  return (
    <Section title="Conformance (DIRECT_V1 wallet)" testId="dev-conformance">
      <p className="note">Runs our suite against your seamless-wallet endpoint with your test player. Passing makes you INTEGRATION_READY; partner certification is a separate record.</p>
      {can(user, 'dev.conformance') && (
        <form
          className="form inline"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            const r = await backend.developer!.post<{ passed: boolean }>('/v1/admin/developer/conformance', { operatorId: v.operatorId, externalPlayerId: player, currency });
            setBusy(false);
            setMsg(r.ok ? (r.value.passed ? 'All checks passed.' : 'Some checks failed — see the report.') : r.error);
            await reload();
          }}
        >
          <label>
            Test player id <input value={player} onChange={(e) => setPlayer(e.target.value)} data-testid="dev-conf-player" />
          </label>
          <label>
            Currency <input value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="dev-conf-currency" />
          </label>
          <button type="submit" className="btn" disabled={busy || !player || !currency} data-testid="dev-conf-run">
            {busy ? 'Running…' : 'Run conformance'}
          </button>
        </form>
      )}
      {msg && <p role="status">{msg}</p>}
      {last?.report ? (
        <>
          <p>
            Last run {last.runId} · {fmtTime(last.startedAt)} · target {last.report.target} ({last.report.targetKind}) · {last.passed ? 'PASSED' : 'FAILED'}
          </p>
          <div className="table-wrap">
            <table data-testid="dev-conf-report">
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Result</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {last.report.checks.map((c) => (
                  <tr key={c.id}>
                    <td>{c.title}</td>
                    <td>
                      <StateTag state={c.result} />
                    </td>
                    <td>{c.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="empty">No conformance run yet.</p>
      )}
    </Section>
  );
}

/**
 * Self-service connection: the partner proposes its wallet base URL, wire scales and webhook hosts (a platform
 * admin approves — PARTNER_CONNECTION) and generates its own wallet secrets, shown only to it.
 */
function Connection({ v, reload }: { v: DevView; reload: () => Promise<void> }) {
  const { backend, user, refresh } = useCtx();
  const dev = backend.developer!;
  const [f, setF] = useState({ baseUrl: v.wallet.baseUrl ?? '', webhookHosts: '', wireScales: Object.entries(v.wallet.wireScales).map(([k, n]) => `${k}=${n}`).join(' '), idempotent: v.wallet.idempotent, statusInquiry: v.wallet.statusInquiry });
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [secrets, setSecrets] = useState<{ keyId: string; requestSecret: string; responseSecret: string } | null>(null);
  const id = useId();
  const mayRequest = can(user, 'request.connection');
  const scales = () => {
    const out: Record<string, number> = {};
    for (const p of f.wireScales.split(/[\s,]+/).filter(Boolean)) {
      const [k, n] = p.split('=');
      if (k && n !== undefined) out[k.toUpperCase()] = Number(n);
    }
    return out;
  };
  const submit = async () => {
    const payload: Record<string, string> = { operatorId: v.operatorId, idempotent: String(f.idempotent), statusInquiry: String(f.statusInquiry) };
    if (f.baseUrl.trim() && f.baseUrl.trim() !== v.wallet.baseUrl) payload.baseUrl = f.baseUrl.trim();
    if (f.webhookHosts.trim()) payload.webhookHosts = f.webhookHosts.trim();
    const sc = scales();
    if (Object.keys(sc).length) payload.wireScales = JSON.stringify(sc);
    const r = await backend.request(user, 'PARTNER_CONNECTION', v.operatorId, `Connection for ${v.operatorId}`, payload, reason);
    if (!r.ok) return setMsg({ ok: false, text: `${r.error.code}: ${r.error.message}` });
    setMsg({ ok: true, text: `${r.value.requestId} sent: a platform admin approves it, then it applies.` });
    setReason('');
    refresh();
  };
  const genSecrets = async () => {
    const r = await dev.post<{ keyId: string; requestSecret: string; responseSecret: string }>(`/v1/admin/operators/${encodeURIComponent(v.operatorId)}/wallet/secrets`, { reason });
    if (!r.ok) return setMsg({ ok: false, text: r.error });
    setSecrets(r.value);
    setReason('');
    await reload();
  };
  const hostsOf = (p: string) => v.hosts.filter((h) => h.purpose === p).map((h) => h.host);
  return (
    <Section title="Wallet connection" testId="dev-connection">
      <p className="lead-text">When a player bets, we call your wallet to take the stake and pay the result. Two things are needed: your wallet address (we approve it) and a pair of signing secrets (you generate them here).</p>
      <dl className="kv">
        <dt>Today</dt>
        <dd>{v.wallet.mode === 'DIRECT' ? <>your wallet {v.wallet.baseUrl && <code>{v.wallet.baseUrl}</code>}</> : 'UrbanRats demo wallet (play money) — fine for first tests'}</dd>
        <dt>Signing secrets</dt>
        <dd>{v.wallet.secretsSet ? <>set · key id <code>{v.wallet.keyId}</code></> : 'not set yet'}</dd>
        <dt>Allowed hosts</dt>
        <dd>
          wallet: {hostsOf('WALLET').join(', ') || '—'} · webhooks: {hostsOf('WEBHOOK').join(', ') || '—'}
        </dd>
      </dl>
      {mayRequest && (
        <div className="form inline">
          <label htmlFor={`${id}-r`}>Note for the audit log (min {MIN_REASON} chars)</label>
          <input id={`${id}-r`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. sandbox wallet of our integration" data-testid="conn-reason" />
        </div>
      )}
      <div className="dv-choices">
        {mayRequest && (
          <div className="dv-choice">
            <h3>A · Wallet address and hosts <span className="tag">UrbanRats approves</span></h3>
            <label htmlFor={`${id}-url`}>Wallet base URL — we add /balance, /bet, /win, /rollback, /status</label>
            <input id={`${id}-url`} value={f.baseUrl} onChange={(e) => setF({ ...f, baseUrl: e.target.value })} placeholder="https://wallet.example.com/urbanrats" data-testid="conn-url" />
            <label htmlFor={`${id}-wh`}>Webhook hosts (optional, space separated)</label>
            <input id={`${id}-wh`} value={f.webhookHosts} onChange={(e) => setF({ ...f, webhookHosts: e.target.value })} placeholder="hooks.example.com" />
            <details>
              <summary>Advanced: decimals and behaviour</summary>
              <label htmlFor={`${id}-ws`}>Decimals per currency on wallet calls (wire scale)</label>
              <input id={`${id}-ws`} value={f.wireScales} onChange={(e) => setF({ ...f, wireScales: e.target.value })} placeholder="EUR=2 TRY=2 USDT=6" />
              <label className="op-check">
                <input type="checkbox" checked={f.idempotent} onChange={(e) => setF({ ...f, idempotent: e.target.checked })} /> A repeated transactionId returns the first result (idempotent)
              </label>
              <label className="op-check">
                <input type="checkbox" checked={f.statusInquiry} onChange={(e) => setF({ ...f, statusInquiry: e.target.checked })} /> We answer /status
              </label>
            </details>
            <button type="button" className="btn primary" disabled={!ok} onClick={() => void submit()} data-testid="conn-request">
              Send for approval
            </button>
          </div>
        )}
        {can(user, 'dev.keys') && (
          <div className="dv-choice">
            <h3>B · Signing secrets <span className="tag">instant</span></h3>
            <p className="muted">Two secrets: we sign our wallet calls with the first, you sign your answers with the second. Shown once, only to you.{v.wallet.secretsSet ? ' Replacing them stops wallet calls until your server uses the new pair.' : ''}</p>
            <button type="button" className="btn" disabled={!ok} onClick={() => void genSecrets()} data-testid="conn-secrets">
              {v.wallet.secretsSet ? 'Replace signing secrets' : 'Generate signing secrets'}
            </button>
          </div>
        )}
      </div>
      {msg && (
        <p role="status" className={msg.ok ? 'status-msg' : 'field-error'}>
          {msg.text}
        </p>
      )}
      {secrets && (
        <div className="warn-box" role="status" data-testid="conn-secret">
          <strong>Wallet signing secrets — shown once. Copy them now into your server's secret store.</strong>
          <p className="note">requestSecret: verify our calls with it. responseSecret: sign your answers with it. keyId arrives as x-ur-key-id on our calls.</p>
          <pre className="code">{`keyId          ${secrets.keyId}\nrequestSecret  ${secrets.requestSecret}\nresponseSecret ${secrets.responseSecret}`}</pre>
          <button type="button" className="btn" onClick={() => setSecrets(null)}>
            I stored them — hide
          </button>
        </div>
      )}
      <h3>Requests</h3>
      <ChangeRequests filter={(r) => r.kind === 'PARTNER_CONNECTION' && r.payload.operatorId === v.operatorId} testId="conn-requests" />
    </Section>
  );
}
